/**
 * Seating for an ad-hoc round.
 *
 * Somebody organizes a game that is not on the schedule — an extra nine, a
 * replay, whoever is still standing after dinner — and the field has to be cut
 * into groups on the spot. The week's real draw is not up for renegotiation, so
 * this deliberately does not touch the optimizer in `pairings.ts`: that one
 * searches every round at once and assumes whole foursomes, neither of which is
 * true here.
 */

// The tiebreaks' PRNG, not a second one. Both exist to make a decision that
// looks arbitrary reproducible, and two copies of a generator is two things
// that can drift.
import { hash32, mulberry32 } from './rank'

/** Smallest group a starter will send off. */
const MIN_GROUP = 2

/** Biggest group the course will let out. */
const MAX_GROUP = 4

/** Used when the caller does not care which of the equally fair seatings it gets. */
const DEFAULT_SEED = 'seating'

export interface SeatablePlayer {
  id: string
  courseHandicap: number
}

/**
 * How many men go in each group, biggest first.
 *
 * Even sizes beat full ones: six men are two threes, never a foursome and a
 * pair trailing it round. So take the fewest groups that fit under the cap and
 * spread the field across them, handing the remainder out one man at a time.
 */
const groupSizes = (n: number): number[] => {
  if (n < MIN_GROUP) return n > 0 ? [n] : []
  const count = Math.ceil(n / MAX_GROUP)
  const base = Math.floor(n / count)
  const remainder = n % count
  return Array.from({ length: count }, (_, i) => base + (i < remainder ? 1 : 0))
}

/**
 * A pair's key, order-independent, so "a with b" and "b with a" are one entry.
 */
const pairKey = (a: string, b: string) => (a < b ? `${a}|${b}` : `${b}|${a}`)

/** How many times each pair has already shared a group this week. */
const sharedCounts = (
  history: readonly (readonly string[])[],
): Map<string, number> => {
  const counts = new Map<string, number>()
  for (const group of history) {
    for (let i = 0; i < group.length; i++) {
      for (let j = i + 1; j < group.length; j++) {
        const key = pairKey(group[i]!, group[j]!)
        counts.set(key, (counts.get(key) ?? 0) + 1)
      }
    }
  }
  return counts
}

/**
 * A repeat costs its count squared, because the complaint is never "we played
 * together once" — it is the fourth time. Sparing one long-suffering pair is
 * worth more than shaving a first meeting off three others.
 */
const REPEAT_WEIGHT = 10

/**
 * A group's handicap average is charged the square of its distance from the
 * field's. Weighted against the repeats so that a group of scratch men costs
 * about what a third meeting costs: both are the kind of thing a man notices
 * on the first tee.
 */
const BALANCE_WEIGHT = 1

interface Field {
  shared: Map<string, number>
  handicapOf: Map<string, number>
  mean: number
}

const cost = (groups: string[][], field: Field): number => {
  let c = 0
  for (const g of groups) {
    for (let i = 0; i < g.length; i++) {
      for (let j = i + 1; j < g.length; j++) {
        c += REPEAT_WEIGHT * (field.shared.get(pairKey(g[i]!, g[j]!)) ?? 0) ** 2
      }
    }
    const total = g.reduce((s, id) => s + (field.handicapOf.get(id) ?? 0), 0)
    c += BALANCE_WEIGHT * (total / g.length - field.mean) ** 2
  }
  return c
}

const shuffled = <T>(xs: readonly T[], rng: () => number): T[] => {
  const a = [...xs]
  for (let i = a.length - 1; i > 0; i--) {
    const j = Math.floor(rng() * (i + 1))
    ;[a[i], a[j]] = [a[j]!, a[i]!]
  }
  return a
}

const chunk = (ids: string[], sizes: number[]): string[][] => {
  const groups: string[][] = []
  let at = 0
  for (const size of sizes) {
    groups.push(ids.slice(at, at + size))
    at += size
  }
  return groups
}

/** Enough restarts to shake off a bad shuffle, few enough to stay instant. */
const RESTARTS = 8

/** A pass that improves nothing ends the climb, so this is only a backstop. */
const MAX_PASSES = 40

/**
 * Swap men between groups for as long as swapping helps.
 *
 * A field this size does not justify annealing: every pairwise swap is cheap to
 * try, so we just try all of them, over and over, until a whole pass finds
 * nothing left to improve.
 */
const improve = (groups: string[][], field: Field): void => {
  let best = cost(groups, field)
  for (let pass = 0; pass < MAX_PASSES; pass++) {
    let moved = false
    for (let gi = 0; gi < groups.length; gi++) {
      for (let gj = gi + 1; gj < groups.length; gj++) {
        const a = groups[gi]!
        const b = groups[gj]!
        for (let pi = 0; pi < a.length; pi++) {
          for (let pj = 0; pj < b.length; pj++) {
            ;[a[pi], b[pj]] = [b[pj]!, a[pi]!]
            const c = cost(groups, field)
            if (c < best) {
              best = c
              moved = true
            } else {
              ;[a[pi], b[pj]] = [b[pj]!, a[pi]!]
            }
          }
        }
      }
    }
    if (!moved) return
  }
}

export function seatRound(
  players: readonly SeatablePlayer[],
  /** Every foursome already drawn this week, as player-id lists. Used to avoid repeat partners. */
  history: readonly (readonly string[])[],
  opts?: { seed?: string },
): string[][] {
  const ids = players.map((p) => p.id)
  const sizes = groupSizes(ids.length)
  // One group or none: there is nothing to weigh, and a lone man still gets a
  // tee time rather than being dropped from his own round.
  if (sizes.length <= 1) return sizes.length ? [ids] : []

  const field: Field = {
    shared: sharedCounts(history),
    handicapOf: new Map(players.map((p) => [p.id, p.courseHandicap])),
    mean: players.reduce((s, p) => s + p.courseHandicap, 0) / players.length,
  }
  let best: string[][] | null = null
  let bestCost = Infinity

  for (let restart = 0; restart < RESTARTS; restart++) {
    const rng = mulberry32(hash32(`${opts?.seed ?? DEFAULT_SEED}#${restart}`))
    const groups = chunk(shuffled(ids, rng), sizes)
    improve(groups, field)
    const c = cost(groups, field)
    if (c < bestCost) {
      bestCost = c
      best = groups
    }
  }

  return best!
}
